/**
 * Canonical email-body HTML sanitization for WTC apps.
 *
 * This is the SINGLE SOURCE of the DOMPurify allow-list + hooks used to render
 * untrusted email HTML (inbound mail, CRM communications). Both `apps/hub`
 * (via `SafeEmailBody`) and `apps/next-email-client` render through the
 * `SafeEmailBody` React component in this package, which sanitizes with
 * {@link sanitizeEmailHtml} before injecting into an iframe `srcDoc`.
 *
 * Pure module (no `"use client"`, no React) so it is safe to import from server
 * code — e.g. Hub's pre-sanitize normalization pipeline re-exports the config
 * and hooks from here to stay in lockstep with what the renderer applies.
 */

import createDOMPurify from "dompurify";

type DomPurifyInstance = ReturnType<typeof createDOMPurify>;
type DomPurifyConfig = Parameters<DomPurifyInstance["sanitize"]>[1];

/**
 * DOMPurify allow-list for email bodies. Permits the structural + inline tags
 * real mail uses (tables, lists, images, links, basic formatting) and strips
 * everything else. `KEEP_CONTENT` preserves visible copy when unwrapping
 * unsupported scaffolding (e.g. Outlook `{mso}`), while
 * {@link registerDomPurifyEmailBodyHooks} clears `<style>`/`<script>` text so
 * that kept content can never surface raw CSS/JS.
 */
export const EMAIL_BODY_DOM_PURIFY_CONFIG: DomPurifyConfig = {
  ALLOWED_TAGS: [
    "p",
    "br",
    "div",
    "span",
    "strong",
    "em",
    "b",
    "i",
    "u",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "ul",
    "ol",
    "li",
    "blockquote",
    "pre",
    "code",
    "table",
    "thead",
    "tbody",
    "tr",
    "td",
    "th",
    "a",
    "img",
    "hr",
  ],
  ALLOWED_ATTR: ["href", "src", "alt", "title", "target", "rel", "width", "height", "style"],
  ALLOWED_URI_REGEXP:
    /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
  KEEP_CONTENT: true,
  RETURN_DOM: false,
  RETURN_DOM_FRAGMENT: false,
  RETURN_TRUSTED_TYPE: false,
};

const DOM_PURIFY_HOOK_FLAG = "__wtcDomPurifyEmailBodyHooksInstalled";

/**
 * Clears `<style>`/`<script>` **contents** before DOMPurify serializes, so
 * `KEEP_CONTENT: true` cannot leak CSS/JS text into the rendered body. Installed
 * once per instance (idempotent via a global flag).
 */
export function registerDomPurifyEmailBodyHooks(purify: DomPurifyInstance): void {
  const g = globalThis as unknown as Record<string, boolean | undefined>;
  if (g[DOM_PURIFY_HOOK_FLAG]) {
    return;
  }
  g[DOM_PURIFY_HOOK_FLAG] = true;
  purify.addHook("uponSanitizeElement", (node) => {
    const tag = node.nodeName?.toUpperCase?.();
    if (tag === "STYLE" || tag === "SCRIPT") {
      node.textContent = "";
    }
  });
}

let domPurifyInstance: DomPurifyInstance | null = null;

function getDomPurifyInstance(): DomPurifyInstance | null {
  // DOMPurify needs a DOM — no-op on the server; the renderer fills srcDoc on the client.
  if (typeof window === "undefined") {
    return null;
  }
  if (domPurifyInstance) {
    return domPurifyInstance;
  }
  const purify = createDOMPurify(window);
  registerDomPurifyEmailBodyHooks(purify);
  domPurifyInstance = purify;
  return purify;
}

/**
 * Sanitize an email-body HTML fragment with the canonical WTC allow-list.
 * Returns `""` on the server (no DOM) or for empty input.
 */
export function sanitizeEmailHtml(html: string): string {
  if (!html) {
    return "";
  }
  const purify = getDomPurifyInstance();
  if (!purify) {
    return "";
  }
  return purify.sanitize(html, EMAIL_BODY_DOM_PURIFY_CONFIG);
}
